import type {
  FleetRun,
  FleetRunner,
  FleetRunnerAction,
  FleetRunnerActionRecord,
  FleetRunnerActionRequest,
  FleetRunnerEditableSettings,
  FleetRunnerSlotsValue
} from './claude-fleet.types'
import { isRunnerOwner } from './claude-fleet.runners'
import type { RunnerRemoteControl } from './claude-fleet.runner-live'
import { machineSettingsWords } from './claude-fleet.machine-settings'
import { formatDateTime } from './claude-fleet.utils'

/**
 * The ⋮ menu on a laptop runner's row. History opens the runner's drawer for anyone. The fleet holds pause and
 * resume itself (it stops offering that laptop work); off, stop a run, renew SSO, nudge and slots wait for the
 * laptop's next one-minute check. Only the laptop's owner and fleet operators may use those, and the fleet refuses
 * anyone else.
 */

export type FleetRunnerMenuChoice =
  | 'pause-1h'
  | 'pause-4h'
  | 'pause-tomorrow'
  | 'pause-usage-reset'
  | 'resume'
  | 'off'
  | 'stop-run'
  | 'hand-back-run'
  | 'renew-sso'
  | 'nudge'
  | 'slots'
  | 'settings'
  | 'update'

/**
 * Every item on the menu: the owner's own /runner window and History, which only open something, the locked line, then
 * the choices sent to the laptop.
 */
export type FleetRunnerMenuKey = 'remote' | 'history' | 'locked' | FleetRunnerMenuChoice

export interface FleetRunnerMenuItem {
  key: FleetRunnerMenuKey
  title: string
  disabled?: boolean
  /** A line that is read, not chosen: Wasabi's flyout renders it as a header. */
  header?: boolean
  /** Why the item does what it does, on hover; under the title when it is off, since a disabled item takes no hover. */
  help?: string
}

/** A laptop whose own SSO has expired cannot be reached to renew it; it renews itself once it can. */
export function isRunnerSsoExpired(runner: Pick<FleetRunner, 'pausedReason'>): boolean {
  return /sso/i.test(runner.pausedReason ?? '') && /expir/i.test(runner.pausedReason ?? '')
}

export const FLEET_RUNNER_ACTION_LABELS: Record<FleetRunnerAction, string> = {
  pause: 'Pause',
  resume: 'Resume',
  off: 'Turn off',
  'stop-run': 'Stop a run',
  'renew-sso': 'Renew SSO',
  nudge: 'Nudge owner',
  slots: 'Slots',
  settings: 'Machine settings',
  update: 'Update runner',
  say: 'Message to the /runner window'
}

/** The most runs a laptop takes at once, fixed or auto (the workspace's bin/laptop_runner.py MAX_SLOTS). */
export const FLEET_RUNNER_MAX_SLOTS = 10

/** What auto takes while the owner's Claude usage cannot be read. */
const AUTO_SLOTS_UNKNOWN_USAGE = 2

/** Slots… offers Auto, then each number a laptop may be fixed at. */
export const FLEET_RUNNER_SLOTS_OPTIONS: { title: string; value: FleetRunnerSlotsValue }[] = [
  { title: 'Auto', value: 'auto' },
  ...Array.from({ length: FLEET_RUNNER_MAX_SLOTS }, (_, index) => ({ title: String(index + 1), value: index + 1 }))
]

/** The laptop's slots setting as its heartbeat reports it, or null from a laptop that sends none. */
export function runnerSlotsSetting(runner: Pick<FleetRunner, 'health'>): FleetRunnerSlotsValue | null {
  const slots = runner.health?.slots
  if (slots?.mode === 'auto') return 'auto'
  return slots?.mode === 'pinned' && typeof slots.pinned === 'number' ? slots.pinned : null
}

/** The facts Slots… shows: the laptop's setting and what it means, and what it takes now and why. */
export function runnerSlotsFacts(runner: Pick<FleetRunner, 'health' | 'slotsLine'>): { setting: string; now: string } {
  const setting = runnerSlotsSetting(runner)
  return {
    setting: setting === 'auto'
      ? `Auto: sized each minute by the owner's Claude usage, up to ${FLEET_RUNNER_MAX_SLOTS} (${AUTO_SLOTS_UNKNOWN_USAGE} while the usage cannot be read)`
      : setting !== null ? `Fixed at ${setting}` : 'This runner has not reported its slots setting',
    now: runner.slotsLine ? `Now: ${runner.slotsLine}` : 'Now: not reported'
  }
}

/** Choices that end work, switch a laptop off or message its owner, so the page asks first. */
export const FLEET_RUNNER_CONFIRMED_CHOICES: FleetRunnerMenuChoice[] = ['off', 'stop-run', 'hand-back-run', 'nudge', 'update']

/** The two choices that end one of the laptop's runs, which open the run picker. */
export const FLEET_RUNNER_RUN_CHOICES: FleetRunnerMenuChoice[] = ['stop-run', 'hand-back-run']

/** Stop ends a run for good; Hand back lets another runner run it. */
const STOP_HELP = 'Ends the run for good. No runner runs it again.'
const HAND_BACK_HELP = 'Ends the run on this runner. The fleet offers it to another runner.'

/** The confirm button for a choice the page asks about: red only where work ends or the laptop switches off. */
export function runnerConfirmButton(choice: FleetRunnerMenuChoice): { confirmText: string; confirmColor: 'error' | 'primary' } {
  if (choice === 'nudge') return { confirmText: 'Send nudge', confirmColor: 'primary' }
  if (choice === 'update') return { confirmText: 'Update', confirmColor: 'primary' }
  if (choice === 'stop-run') return { confirmText: 'Stop for good', confirmColor: 'error' }
  if (choice === 'hand-back-run') return { confirmText: 'Hand back', confirmColor: 'error' }
  return { confirmText: 'Yes', confirmColor: 'error' }
}

export const FLEET_RUNNER_MENU_LOCKED_REASON = 'Only this runner\'s owner and fleet operators can act on it.'

export const FLEET_RUNNER_LOCKED_ITEM: FleetRunnerMenuItem = { key: 'locked', title: FLEET_RUNNER_MENU_LOCKED_REASON, header: true }

/** Whether this person may act on the laptop: its owner, or a fleet operator. */
export function canActOnRunner(
  runner: Pick<FleetRunner, 'owner'>,
  viewer: { email: string | null | undefined; operators: string[] }
): boolean {
  const email = viewer.email?.trim().toLowerCase()
  if (!email) return false
  return isRunnerOwner(runner.owner, email) || viewer.operators.some(operator => operator.toLowerCase() === email)
}

export function isRunnerPausedFromPage(runner: Pick<FleetRunner, 'pausedUntil'>, now: number): boolean {
  if (!runner.pausedUntil) return false
  const until = Date.parse(runner.pausedUntil)
  return Number.isFinite(until) && until > now
}

/** The runs this laptop is working on now, for Stop a run. */
export function runnerRunningRuns(runner: Pick<FleetRunner, 'owner'>, runs: FleetRun[]): FleetRun[] {
  const owner = runner.owner.toLowerCase()
  return runs.filter(run => run.ranOn === 'laptop' && run.runnerOwner?.toLowerCase() === owner && run.state === 'running')
}

export const FLEET_RUNNER_HISTORY_ITEM: FleetRunnerMenuItem = {
  key: 'history',
  title: 'History',
  help: 'What this runner is working on, its recent runs and what it reported lately.'
}

/**
 * The menu for one laptop: its /runner window in Claude (`remote`, passed only to its owner), History, then its
 * actions, with what does not apply right now left disabled rather than hidden. A viewer who may not act on the laptop
 * (`canAct` false) sees every action disabled, under one line saying why.
 */
export function runnerMenuItems(
  runner: Pick<FleetRunner, 'live' | 'pausedUntil' | 'pausedReason' | 'actions' | 'health'>,
  options: { now: number; runningRuns: number; canAct: boolean; remote?: RunnerRemoteControl | null }
): FleetRunnerMenuItem[] {
  const opens = options.remote ? [remoteItem(options.remote), FLEET_RUNNER_HISTORY_ITEM] : [FLEET_RUNNER_HISTORY_ITEM]
  const actions = runnerActionItems(runner, options)
  if (options.canAct) return [...opens, ...actions]
  const locked = actions.map(({ help: _help, ...item }) => ({ ...item, disabled: true }))
  return [...opens, FLEET_RUNNER_LOCKED_ITEM, ...locked]
}

/** Claude on the web, in a new tab: the session itself when its address is known, else the list to pick it from by name. */
function remoteItem(remote: RunnerRemoteControl): FleetRunnerMenuItem {
  return {
    key: 'remote',
    title: 'Open your /runner window in Claude',
    help: remote.listed
      ? `Opens claude.ai/code in a new tab: pick the session named "${remote.name}".`
      : 'Opens its Remote Control session on claude.ai in a new tab.'
  }
}

const UPDATE_HELP = 'Its /runner window finishes the jobs it is running, then restarts on the latest workspace and plugins.'
const NOTHING_TO_UPDATE = 'Nothing waits for this runner: it runs the latest, or it is headless and each job starts on what is on disk.'

/** Update runner as the fleet offers it: only while an update waits for this runner's /runner window. */
function updateItem(runner: Pick<FleetRunner, 'actions'>): FleetRunnerMenuItem {
  const offer = runner.actions?.find(action => action.id === 'update')
  return { key: 'update', title: 'Update runner', disabled: !offer?.enabled, help: offer ? offer.why || UPDATE_HELP : NOTHING_TO_UPDATE }
}

const SETTINGS_HELP = 'Its auto-update, VPN reconnect, free disk floor and update answers, as bin/runner.sh sets them.'
const SETTINGS_TOO_OLD = 'This runner does not report its settings yet; it can be changed from ADX once its workspace is updated.'

function runnerActionItems(
  runner: Pick<FleetRunner, 'live' | 'pausedUntil' | 'pausedReason' | 'actions' | 'health'>,
  options: { now: number; runningRuns: number }
): FleetRunnerMenuItem[] {
  const paused = isRunnerPausedFromPage(runner, options.now)
  return [
    { key: 'pause-1h', title: 'Pause for 1 hour', disabled: !runner.live },
    { key: 'pause-4h', title: 'Pause for 4 hours', disabled: !runner.live },
    { key: 'pause-tomorrow', title: 'Pause until tomorrow', disabled: !runner.live },
    { key: 'resume', title: 'Resume', disabled: !paused },
    { key: 'slots', title: 'Slots…', help: 'How many runs this runner takes at once: a fixed number, or auto.' },
    runner.health?.machineSettings
      ? { key: 'settings', title: 'Machine settings…', help: SETTINGS_HELP }
      : { key: 'settings', title: 'Machine settings…', disabled: true, help: SETTINGS_TOO_OLD },
    { key: 'stop-run', title: 'Stop a run…', disabled: options.runningRuns === 0, help: STOP_HELP },
    { key: 'hand-back-run', title: 'Hand back a run…', disabled: options.runningRuns === 0, help: HAND_BACK_HELP },
    isRunnerSsoExpired(runner)
      ? { key: 'renew-sso', title: 'Renew AWS SSO', disabled: true, help: 'This runner\'s SSO has already ended, so the fleet cannot reach it. The runner sends its owner a sign-in link in Slack by itself; nudge the owner if they have not used it.' }
      : { key: 'renew-sso', title: 'Renew AWS SSO', help: 'Sends the owner a sign-in link in Slack, so they can renew AWS SSO from their phone before it ends.' },
    { key: 'nudge', title: 'Nudge the owner', help: 'A desktop notification and a DM saying why the runner is idle and what to do.' },
    updateItem(runner),
    { key: 'off', title: 'Turn off this runner', disabled: !runner.live }
  ]
}

/**
 * The request a menu choice sends; `runId` and `byName` only for Stop or Hand back a run, `value` only for Slots,
 * `settings` only for Machine settings.
 */
export function runnerActionRequest(
  choice: FleetRunnerMenuChoice,
  options: { runId?: string; value?: FleetRunnerSlotsValue; settings?: Partial<FleetRunnerEditableSettings>; byName?: string } = {}
): FleetRunnerActionRequest {
  switch (choice) {
    case 'pause-1h':
      return { action: 'pause', hours: 1 }
    case 'pause-4h':
      return { action: 'pause', hours: 4 }
    case 'pause-tomorrow':
      return { action: 'pause', until: 'tomorrow' }
    case 'pause-usage-reset':
      return { action: 'pause', until: 'usage-reset' }
    case 'stop-run':
    case 'hand-back-run':
      return {
        action: 'stop-run',
        runId: options.runId,
        intent: choice === 'stop-run' ? 'stop' : 'hand-back',
        byName: options.byName
      }
    case 'slots':
      return { action: 'slots', value: options.value }
    case 'settings':
      return { action: 'settings', settings: options.settings }
    default:
      return { action: choice }
  }
}

function clock(iso: string): string {
  return formatDateTime(iso) || iso
}

/** A slots value is short and says what was set; a message's text is the window conversation's to show, not this line's. */
function recordValue(record: FleetRunnerActionRecord): string {
  if (!record.value || record.action === 'say') return ''
  return record.action === 'settings' ? ` (${machineSettingsWords(record.value)})` : ` ${record.value}`
}

function recordText(record: FleetRunnerActionRecord): string {
  const by = record.by ? ` by ${record.by}` : ''
  const value = recordValue(record)
  return `${FLEET_RUNNER_ACTION_LABELS[record.action] ?? record.action}${value}${by}, ${clock(record.at)}`
}

/** How long the last applied action stays under the owner: after a day it is old news, and "Turn off" read as current. */
export const LAST_ACTION_SHOWN_MS = 24 * 60 * 60 * 1000

/** Each action as something that already happened, for the last-action line. */
const FLEET_RUNNER_ACTION_PAST: Record<FleetRunnerAction, string> = {
  pause: 'paused',
  resume: 'resumed',
  off: 'turned off',
  'stop-run': 'stopped a run',
  'renew-sso': 'sent an SSO sign-in link',
  nudge: 'nudged the owner',
  slots: 'set slots',
  settings: 'changed its settings',
  update: 'asked it to update',
  say: 'sent a message to the /runner window'
}

function day(iso: string): string {
  const when = new Date(iso)
  return Number.isNaN(when.getTime()) ? iso : when.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/** "Last action: turned off, Oct 13 (done)", while it is under a day old; null after. */
function lastActionText(record: FleetRunnerActionRecord, now: number): string | null {
  const at = Date.parse(record.at)
  if (!Number.isFinite(at) || now - at > LAST_ACTION_SHOWN_MS) return null
  const value = recordValue(record)
  const by = record.by ? ` by ${record.by}` : ''
  const result = !record.result ? '' : record.result === 'ok' ? ' (done)' : ` (${record.result})`
  return `Last action: ${FLEET_RUNNER_ACTION_PAST[record.action] ?? record.action}${value}${by}, ${day(record.at)}${result}`
}

/** The line under a laptop's owner: paused from the page, an action on its way, or the last one applied. */
export function runnerActionStatus(
  runner: Pick<FleetRunner, 'pausedUntil' | 'pendingAction' | 'lastAction'>,
  now: number
): string | null {
  if (runner.pendingAction) return `Sent: ${recordText(runner.pendingAction)}. The runner applies it within a minute.`
  if (runner.pausedUntil && isRunnerPausedFromPage(runner, now)) return `Paused until ${clock(runner.pausedUntil)}`
  return runner.lastAction ? lastActionText(runner.lastAction, now) : null
}

/** What the confirm and the toast say for a choice. */
export function runnerChoiceText(choice: FleetRunnerMenuChoice, owner: string): { title: string; message: string; done: string } {
  const texts: Record<FleetRunnerMenuChoice, { title: string; message: string; done: string }> = {
    'pause-1h': { title: 'Pause for 1 hour?', message: `${owner}'s machine takes no new fleet work for an hour. Runs already going finish.`, done: 'Paused for 1 hour' },
    'pause-4h': { title: 'Pause for 4 hours?', message: `${owner}'s machine takes no new fleet work for 4 hours. Runs already going finish.`, done: 'Paused for 4 hours' },
    'pause-tomorrow': { title: 'Pause until tomorrow?', message: `${owner}'s machine takes no new fleet work until midnight tonight, New York time. Runs already going finish.`, done: 'Paused until tomorrow' },
    'pause-usage-reset': { title: 'Pause until the 5-hour window resets?', message: `${owner}'s machine takes no new fleet work until their 5-hour Claude window resets. Runs already going finish.`, done: 'Paused until the 5-hour window resets' },
    resume: { title: 'Resume?', message: `${owner}'s machine takes fleet work again.`, done: 'Resumed' },
    off: { title: 'Turn this runner off?', message: `${owner}'s machine stops being a runner at its next check, within a minute, as if they ran /runner off. Runs already going finish. Only they can turn it back on.`, done: 'Turning off' },
    'stop-run': { title: 'Stop this run for good?', message: `${owner}'s runner ends the run within a minute, and no runner runs it again. Anything it already pushed or posted stays. To have another runner run it, use Hand back a run instead.`, done: 'Stopping the run' },
    'hand-back-run': { title: 'Hand back this run?', message: `${owner}'s runner ends the run within a minute, and the fleet offers it to another runner. Anything it already pushed or posted stays.`, done: 'Handing the run back' },
    'renew-sso': { title: 'Renew AWS SSO?', message: `${owner} gets a Slack DM with an AWS sign-in link to approve, from their phone or computer. Their runner keeps working until the session ends.`, done: 'Sign-in link sent' },
    nudge: { title: 'Nudge the owner?', message: `${owner} gets a desktop notification and a DM saying why their runner is idle and what to do.`, done: 'Nudge sent' },
    update: { title: 'Update this runner?', message: `${owner}'s /runner window takes no new fleet work, lets its running jobs finish, then restarts on the latest workspace and plugins and carries on its conversation, as /runner update does.`, done: 'Update sent' },
    settings: { title: 'Machine settings', message: `${owner}'s machine's own settings. The runner applies a change within a minute, as if they ran bin/runner.sh with the matching flag; runs already going finish.`, done: 'Settings sent' },
    slots: { title: 'Slots', message: `How many fleet runs ${owner}'s machine takes at once. A number fixes it at that number (2 by default); auto sizes it each minute by their Claude usage, up to ${FLEET_RUNNER_MAX_SLOTS} (${AUTO_SLOTS_UNKNOWN_USAGE} while the usage cannot be read). The runner applies it within a minute, as if they ran bin/runner.sh --slots, and runs already going finish.`, done: 'Slots sent' }
  }
  return texts[choice]
}
